import React, { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import UserAccessDialog from "@/components/UserAccessDialog";
import { KeyRound, Users, Loader2, SlidersHorizontal } from "lucide-react";

export default function Settings() {
  const { user, account, isOwner } = useAuth();
  return (
    <div data-testid="settings-page" className="space-y-6 max-w-3xl">
      <div>
        <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: "Manrope" }}>Settings</h1>
        <p className="text-slate-500 mt-1">Your profile, security and team.</p>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-4">
        <h2 className="text-lg font-bold text-slate-900" style={{ fontFamily: "Manrope" }}>Profile</h2>
        <Row label="Name" value={user?.name} />
        <Row label="Email" value={user?.email} />
        <Row label="Role" value={<span className="capitalize">{user?.role}</span>} />
      </div>

      <ChangePasswordCard />

      {isOwner && <TeamCard accountId={user?.account_id} />}

      {account && (
        <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-4">
          <h2 className="text-lg font-bold text-slate-900" style={{ fontFamily: "Manrope" }}>Account</h2>
          <Row label="Company" value={account.company_name} />
          <Row label="BoxHero ID" value={<code className="text-blue-700 font-mono">{account.boxhero_account_id}</code>} />
          <Row label="Primary Email" value={account.primary_email} />
          <Row label="Billing Account" value={account.stripe_customer_id ? <span className="text-green-600 font-medium">Connected</span> : <em className="text-slate-400">not linked</em>} />
          <Row label="Shipping Account" value={account.shipstation_store_id ? <span className="text-green-600 font-medium">Connected</span> : <em className="text-slate-400">not linked</em>} />
        </div>
      )}
    </div>
  );
}

function ChangePasswordCard() {
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 6) { toast.error("New password must be at least 6 characters"); return; }
    if (next !== confirm) { toast.error("Passwords don't match"); return; }
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: next });
      toast.success("Password changed");
      setCur(""); setNext(""); setConfirm("");
    } catch (e2) {
      toast.error(formatApiError(e2));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="bg-white border border-slate-200 rounded-xl p-6 space-y-4" data-testid="change-password-card">
      <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2" style={{ fontFamily: "Manrope" }}>
        <KeyRound className="w-5 h-5 text-blue-600" /> Change password
      </h2>
      <div className="grid sm:grid-cols-3 gap-3">
        <input type="password" value={cur} onChange={(e) => setCur(e.target.value)} required placeholder="Current password" data-testid="cp-current"
          className="px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-400/40" />
        <input type="password" value={next} onChange={(e) => setNext(e.target.value)} required placeholder="New password" data-testid="cp-new"
          className="px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-400/40" />
        <input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required placeholder="Confirm new" data-testid="cp-confirm"
          className="px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-400/40" />
      </div>
      <button type="submit" disabled={saving} data-testid="cp-submit"
        className="bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white text-sm font-semibold px-5 py-2.5 rounded-lg inline-flex items-center gap-2">
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Update password
      </button>
    </form>
  );
}

function TeamCard() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/team/users").then(({ data }) => setUsers(data.users || [])).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6" data-testid="team-card">
      <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2 mb-1" style={{ fontFamily: "Manrope" }}>
        <Users className="w-5 h-5 text-blue-600" /> Your team
      </h2>
      <p className="text-sm text-slate-500 mb-4">Control what each teammate can see and manage. Keep billing to yourself if you'd rather employees not see pricing.</p>
      {loading ? (
        <div className="flex items-center gap-2 text-slate-400 text-sm py-6"><Loader2 className="w-4 h-4 animate-spin" /> Loading team…</div>
      ) : users.length === 0 ? (
        <p className="text-sm text-slate-400 py-4">No teammates yet. Ask BoxHero to invite people to your account.</p>
      ) : (
        <div className="divide-y divide-slate-100">
          {users.map((u) => (
            <div key={u.id} className="flex items-center justify-between py-3" data-testid={`team-user-${u.id}`}>
              <div>
                <p className="text-sm font-semibold text-slate-800">{u.name || u.email}</p>
                <p className="text-xs text-slate-400">{u.email} · <span className="capitalize">{u.role}</span></p>
              </div>
              <button onClick={() => setEditing(u)} data-testid={`team-access-${u.id}`}
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-blue-600 hover:bg-blue-50 px-3 py-1.5 rounded-md">
                <SlidersHorizontal className="w-4 h-4" /> Access
              </button>
            </div>
          ))}
        </div>
      )}
      {editing && <UserAccessDialog user={editing} mode="team" onClose={() => setEditing(null)} onSaved={load} />}
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between text-sm border-b border-slate-100 last:border-0 py-2">
      <span className="text-slate-500">{label}</span>
      <span className="font-medium text-slate-900">{value}</span>
    </div>
  );
}
